import React, { useState, useCallback, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Modal, Button, Form } from 'react-bootstrap';
import axios from 'axios';

const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const GOOGLE_CLIENT_ID = '376001120078-glnpjuqdkqgi79hul5t9j8boug66etem.apps.googleusercontent.com';
const GOOGLE_REDIRECT_URI = 'https://callmanager.ai/oauth/callback';

const CalendarComponent = ({ onAppointmentScheduled }) => {
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  useEffect(() => {
    // Check for access token in URL after OAuth redirect
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('access_token');
    if (token) {
      setAccessToken(token);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleGoogleLogin = () => {
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${GOOGLE_REDIRECT_URI}&response_type=token&scope=https://www.googleapis.com/auth/calendar`;
    window.location.href = authUrl;
  };

  const handleSelectSlot = useCallback(({ start, end }) => {
    if (!accessToken) {
      alert('Please login with Google first');
      handleGoogleLogin();
      return;
    }
    setSelectedSlot({ start, end });
    setShowModal(true);
  }, [accessToken]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Call your backend API to schedule the appointment
      const response = await axios.post('https://us-central1-callmanager-ai.cloudfunctions.net/scheduleAppointment', {
        ...formData,
        startTime: selectedSlot.start,
        endTime: selectedSlot.end
      }, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.data.success) {
        // Add the new event to the calendar
        const newEvent = {
          title: `Meeting with ${formData.name}`,
          start: selectedSlot.start,
          end: selectedSlot.end,
          description: formData.notes
        };
        
        setEvents(prev => [...prev, newEvent]);
        setShowModal(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          notes: ''
        });
        
        if (onAppointmentScheduled) {
          onAppointmentScheduled(newEvent);
        }
      }
    } catch (error) {
      console.error('Failed to schedule appointment:', error);
      if (error.response?.status === 401) {
        alert('Your session has expired. Please login again.');
        handleGoogleLogin();
      } else {
        alert('Failed to schedule appointment. Please try again.');
      }
    }
  };

  return (
    <div>
      {!accessToken && (
        <div className="text-center mb-4">
          <Button variant="primary" onClick={handleGoogleLogin}>
            Login with Google to Schedule
          </Button>
        </div>
      )}
      
      <div style={{ height: '500px' }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          selectable
          onSelectSlot={handleSelectSlot}
          views={['month', 'week', 'day']}
        />

        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Schedule Appointment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <div className="d-flex justify-content-end">
                <Button variant="secondary" className="me-2" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  Schedule
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default CalendarComponent; 